import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import './SearchableCompanySelect.css';

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const companyName = (company) => company?.tradeName || company?.corporateName || 'Empresa sem nome';
const companyStatus = (company) => String(company?.status || '').toUpperCase() === 'INACTIVE'
  ? { label: 'Inativa', className: 'is-inactive' }
  : { label: 'Ativa', className: 'is-active-status' };

export default function SearchableCompanySelect({
  companies = [],
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Digite para pesquisar a empresa...',
  emptyLabel = 'Selecione uma empresa',
  className = '',
  ariaLabel = 'Empresa',
}) {
  const inputRef = useRef(null);
  const listboxIdRef = useRef(`ya-company-list-${Math.random().toString(36).slice(2, 10)}`);
  const listboxId = listboxIdRef.current;
  const selectedCompany = useMemo(
    () => companies.find((company) => Number(company?.id) === Number(value)) || null,
    [companies, value],
  );
  const [query, setQuery] = useState(() => selectedCompany ? companyName(selectedCompany) : '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) setQuery(selectedCompany ? companyName(selectedCompany) : '');
  }, [selectedCompany, open]);

  const allMatches = useMemo(() => {
    const term = normalizeText(query);
    return [...companies]
      .sort((a, b) => companyName(a).localeCompare(companyName(b), 'pt-BR', { sensitivity: 'base' }))
      .filter((company) => {
        if (!term) return true;
        const haystack = normalizeText([
          company?.tradeName,
          company?.corporateName,
          company?.cnpj,
          company?.city,
          company?.neighborhood,
        ].filter(Boolean).join(' '));
        return haystack.includes(term);
      });
  }, [companies, query]);

  const visibleCompanies = useMemo(() => allMatches.slice(0, 100), [allMatches]);

  useEffect(() => {
    setActiveIndex((current) => {
      if (!visibleCompanies.length) return -1;
      return current >= 0 && current < visibleCompanies.length ? current : 0;
    });
  }, [visibleCompanies.length]);

  const selectCompany = (company) => {
    const nextValue = company ? String(company.id) : '';
    setQuery(company ? companyName(company) : '');
    setOpen(false);
    setActiveIndex(0);
    if (String(value || '') !== nextValue) onChange?.(nextValue);
  };

  const handleInputChange = (event) => {
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    setActiveIndex(0);
    if (!selectedCompany) return;
    if (normalizeText(next) !== normalizeText(companyName(selectedCompany))) onChange?.('');
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => visibleCompanies.length ? Math.min(Math.max(current, 0) + 1, visibleCompanies.length - 1) : -1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => visibleCompanies.length ? Math.max(current - 1, 0) : -1);
      return;
    }
    if (event.key === 'Enter' && open && activeIndex >= 0 && visibleCompanies[activeIndex]) {
      event.preventDefault();
      selectCompany(visibleCompanies[activeIndex]);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery(selectedCompany ? companyName(selectedCompany) : '');
    }
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      setOpen(false);
      setQuery(selectedCompany ? companyName(selectedCompany) : '');
    }, 120);
  };

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((current) => {
      const next = !current;
      if (next && selectedCompany) setQuery('');
      if (next) window.setTimeout(() => inputRef.current?.focus(), 0);
      return next;
    });
  };

  return <div className={`ya-company-search ${open ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
    <div className="ya-company-search__control">
      <span className="ya-company-search__icon" aria-hidden="true"><FaSearch /></span>
      <input
        ref={inputRef}
        type="text"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        onFocus={(event) => { setOpen(true); event.currentTarget.select(); }}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      <div className="ya-company-search__actions" aria-label="Ações da empresa">
        {value && !disabled && <button
          type="button"
          className="ya-company-search__clear"
          aria-label="Limpar empresa selecionada"
          title="Limpar empresa"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => selectCompany(null)}
        ><span aria-hidden="true">×</span></button>}
        <button
          type="button"
          className="ya-company-search__toggle"
          disabled={disabled}
          aria-label={open ? 'Fechar lista de empresas' : 'Abrir lista de empresas'}
          aria-expanded={open}
          aria-controls={listboxId}
          title={open ? 'Fechar lista de empresas' : 'Abrir lista de empresas'}
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleOpen}
        ><span aria-hidden="true">{open ? '▴' : '▾'}</span></button>
      </div>
    </div>

    {open && !disabled && <div id={listboxId} className="ya-company-search__menu" role="listbox" aria-label="Empresas disponíveis">
      <button type="button" className={`ya-company-search__option ya-company-search__option--empty ${!value ? 'is-selected' : ''}`} onMouseDown={(event) => event.preventDefault()} onClick={() => selectCompany(null)}>
        <strong>{emptyLabel}</strong>
      </button>
      {visibleCompanies.map((company, index) => {
        const selected = Number(company.id) === Number(value);
        const status = companyStatus(company);
        return <button
          type="button"
          role="option"
          aria-selected={selected}
          key={company.id}
          className={`ya-company-search__option ${selected ? 'is-selected' : ''} ${index === activeIndex ? 'is-active' : ''}`}
          onMouseEnter={() => setActiveIndex(index)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => selectCompany(company)}
        >
          <span><strong>{companyName(company)}</strong><em className={status.className}>{status.label}</em></span>
        </button>;
      })}
      {!visibleCompanies.length && <div className="ya-company-search__empty">Nenhuma empresa encontrada para “{query}”.</div>}
      {allMatches.length > visibleCompanies.length && <div className="ya-company-search__hint">Mostrando 100 de {allMatches.length} empresas. Continue digitando para refinar.</div>}
    </div>}
  </div>;
}
